import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Divider,
  Button,
  Chip,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DescriptionIcon from '@mui/icons-material/Description';
import AddCardIcon from '@mui/icons-material/AddCard';
import SecurityIcon from '@mui/icons-material/Security';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { Document, pdfjs, Page as Pages } from 'react-pdf';
import { useDispatch } from 'react-redux';
import CustomDialog from '../../components/CustomDialogue';
import {
  getProfileList,
  getPaymentMethod,
  createPaymentMethod,
  updatePaymentMethod,
  getPaymentTypes
} from '../../store/Slice/Payout/PayoutSlice';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { PreviewImageApi } from '../../store/Slice/Global/GlobalSlice';
import { normalizeValue } from '../../utils/CommonFunction';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function Profile() {
  const [HistoryPopup, setHistoryPopup] = useState(false);
  const dispatch = useDispatch();

  const { LoginData } = useLoginSelector();
  const { PayoutProfileData, PaymentMethodData, PaymentTypesData } = usePayoutSelector();

  const [paymentForm, setPaymentForm] = useState({
    paymentType: 'BANK_TRANSFER',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    upiId: '',
    paymentMethodId: null
  });

  const [isEdit, setIsEdit] = useState(false);

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [previewFileType, setPreviewFileType] = useState('image');
  const [pageNumber, setPageNumber] = useState(1);

  const handlePreviewDoc = async (doc) => {
    const blob = await dispatch(PreviewImageApi(doc?.documentUrl, null, true));

    if (!blob) {
      console.error('No blob returned from PreviewImageApi');
      return;
    }

    setPreviewFileType(blob.type === 'application/pdf' ? 'pdf' : 'image');
    const fileURL = URL.createObjectURL(blob);
    setPreviewUrl(fileURL);
    setOpenPreview(true);
  };

  const onDocumentLoadSuccess = useCallback(() => {
    setPageNumber(1);
  }, []);

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleDoubleClick = () => {
    if (zoomLevel === 1) {
      setZoomLevel(2);
    } else {
      setZoomLevel(1);
    }
  };

  const getProfileFileList = useCallback(() => {
    if (!LoginData?.userId) return;
    const credential = {
      userId: LoginData?.userId
    };
    dispatch(getProfileList(credential));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getProfileFileList();
    if (LoginData?.userId) {
      dispatch(getPaymentMethod(LoginData.userId));
      dispatch(getPaymentTypes());
    }
  }, [LoginData?.userId, dispatch, getProfileFileList]);

  const handleOpenPaymentDialog = (edit = false) => {
    if (edit && PaymentMethodData) {
      setPaymentForm({
        paymentType: PaymentMethodData.paymentType || 'BANK_TRANSFER',
        accountNumber: PaymentMethodData.accountNumber || '',
        ifscCode: PaymentMethodData.ifscCode || '',
        accountHolderName: PaymentMethodData.accountHolderName || '',
        upiId: PaymentMethodData.upiId || '',
        paymentMethodId: PaymentMethodData.paymentMethodId
      });
      setIsEdit(true);
    } else {
      setPaymentForm({
        paymentType: 'BANK_TRANSFER',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        upiId: '',
        paymentMethodId: null
      });
      setIsEdit(false);
    }
    setHistoryPopup(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePaymentMethod = async () => {
    const payload = {
      ...paymentForm,
      userId: LoginData?.userId
    };

    let res;
    if (isEdit) {
      res = await dispatch(updatePaymentMethod(payload));
    } else {
      res = await dispatch(createPaymentMethod(payload));
    }

    if (res) {
      setHistoryPopup(false);
      dispatch(getPaymentMethod(LoginData?.userId));
    }
  };

  const handleCloseHistoryDialog = () => {
    setHistoryPopup(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        {/* Personal Details */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              height: '100%'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(19, 81, 157, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#13519d'
                }}
              >
                <PersonIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Personal Details
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  User ID
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.user?.userId || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  User Type
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.user?.userType || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Email
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.user?.email || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Mobile
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.user?.mobile || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Joining Date
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.accountOverview?.userjoiningDate || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Manager Name
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.user?.managerName || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Sales Manager Name
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.user?.salesManagerName || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Payment Details */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              height: '100%'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(19, 81, 157, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#13519d'
                }}
              >
                <PaymentIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Payment Overview
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Last Payment Date
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                  {PayoutProfileData?.paymentDetails?.lastPaymentDate || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Total Advance Taken
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#d97706', mt: 0.5 }}>
                  {normalizeValue(PayoutProfileData?.paymentDetails?.totalAdvanceTaken, '—')}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase'
                  }}
                >
                  Total Commission Earned
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#16a34a', mt: 0.5 }}>
                  {normalizeValue(PayoutProfileData?.paymentDetails?.totalCommissionEarned, '—')}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Payment Method Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              height: '100%'
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: 'rgba(19, 81, 157, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#13519d'
                  }}
                >
                  <AddCardIcon fontSize="small" />
                </Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                  Payment Method
                </Typography>
              </Stack>
              {PaymentMethodData && (
                <IconButton
                  sx={{
                    color: '#13519d',
                    backgroundColor: 'rgba(19, 81, 157, 0.08)',
                    borderRadius: '8px',
                    p: 0.75,
                    '&:hover': { backgroundColor: 'rgba(19, 81, 157, 0.16)' }
                  }}
                  onClick={() => handleOpenPaymentDialog(true)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>

            {PaymentMethodData ? (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #13519d 0%, #1e293b 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 160,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 8px 24px rgba(19, 81, 157, 0.2)'
                }}
              >
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="subtitle2"
                      sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: '0.5px' }}
                    >
                      {PaymentMethodData.paymentType === 'BANKTRANSFER' ||
                      PaymentMethodData.paymentType === 'BANK_TRANSFER' ||
                      PaymentMethodData.paymentType === 'NEFT' ||
                      PaymentMethodData.paymentType === 'RTGS' ||
                      PaymentMethodData.paymentType === 'IMPS'
                        ? 'BANK ACCOUNT'
                        : 'UPI ID'}
                    </Typography>
                    <PaymentIcon sx={{ opacity: 0.8 }} />
                  </Stack>
                  <Typography sx={{ mt: 1, fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
                    {PaymentMethodData.accountHolderName}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {PaymentMethodData.paymentType === 'BANKTRANSFER' ||
                  PaymentMethodData.paymentType === 'BANK_TRANSFER' ||
                  PaymentMethodData.paymentType === 'NEFT' ||
                  PaymentMethodData.paymentType === 'RTGS' ||
                  PaymentMethodData.paymentType === 'IMPS' ? (
                    <>
                      <Typography sx={{ fontSize: 18, letterSpacing: 2, fontWeight: 700 }}>
                        {PaymentMethodData.accountNumber
                          ? `•••• •••• ${PaymentMethodData.accountNumber.slice(-4)}`
                          : '•••• •••• ••••'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}
                      >
                        IFSC: {PaymentMethodData.ifscCode}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                      {PaymentMethodData.upiId}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  px: 2,
                  border: '2px dashed #e2e8f0',
                  borderRadius: '12px',
                  bgcolor: '#fafbfc'
                }}
              >
                <AddCardIcon sx={{ fontSize: 36, color: '#94a3b8', mb: 1.5 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#64748b', mb: 2 }}>
                  No Payment Method Configured
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCardIcon />}
                  onClick={() => handleOpenPaymentDialog(false)}
                  sx={{
                    bgcolor: '#13519d',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#0d3d75' }
                  }}
                >
                  Add Payment Method
                </Button>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Documents */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              height: '100%'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: 'rgba(19, 81, 157, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#13519d'
                }}
              >
                <DescriptionIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                Documents & Access
              </Typography>
            </Stack>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {(!PayoutProfileData?.documents || PayoutProfileData.documents.length === 0) && (
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                  No documents uploaded
                </Typography>
              )}
              {PayoutProfileData?.documents?.map((title, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    px: 2,
                    borderRadius: '10px',
                    borderColor: '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#fafbfc'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <DescriptionIcon sx={{ fontSize: 20, color: '#13519d' }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                      {title?.documentTypeCode || 'Document'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label="Verified"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(22, 163, 74, 0.1)',
                        color: '#16a34a',
                        fontWeight: 700,
                        fontSize: 11
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handlePreviewDoc(title)}
                      sx={{
                        color: '#13519d',
                        bgcolor: 'rgba(19, 81, 157, 0.08)',
                        borderRadius: '6px',
                        '&:hover': { bgcolor: 'rgba(19, 81, 157, 0.16)' }
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <SecurityIcon sx={{ fontSize: 18, color: '#13519d' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                Portal Access Levels
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(!PayoutProfileData?.accessPages || PayoutProfileData.accessPages.length === 0) && (
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                  No special access configured
                </Typography>
              )}
              {PayoutProfileData?.accessPages?.map((item) => (
                <Chip
                  key={item?.pageId}
                  label={item?.pageName}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(19, 81, 157, 0.08)',
                    color: '#13519d',
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: '6px'
                  }}
                />
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Document Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
            Document Preview
          </Typography>
          <IconButton size="small" onClick={() => setOpenPreview(false)} sx={{ color: '#64748b' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'auto',
            bgcolor: '#f1f5f9',
            minHeight: '50vh',
            p: 3
          }}
        >
          {previewUrl && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%'
              }}
            >
              {previewFileType === 'pdf' ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    maxHeight: '70vh',
                    overflow: 'auto'
                  }}
                >
                  <Document
                    file={previewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div>Loading PDF...</div>}
                  >
                    <Pages
                      pageNumber={pageNumber}
                      scale={zoomLevel}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  onDoubleClick={handleDoubleClick}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 500,
                    objectFit: 'contain',
                    transform: `scale(${zoomLevel})`,
                    transition: 'transform 0.3s ease-in-out',
                    borderRadius: '8px',
                    cursor: 'zoom-in',
                    userSelect: 'none'
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={handleResetZoom}
            variant="outlined"
            size="small"
            sx={{ borderRadius: '8px' }}
          >
            Reset Zoom
          </Button>
          <Button
            onClick={() => {
              setOpenPreview(false);
              handleResetZoom();
            }}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '8px',
              bgcolor: '#13519d',
              '&:hover': { bgcolor: '#0d3d75' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Payment Method Dialog */}
      <CustomDialog
        showCloseIcon
        open={HistoryPopup}
        maxWidth="sm"
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseHistoryDialog();
          }
        }}
        title={isEdit ? 'Edit Payment Method' : 'Add Payment Method'}
        content={
          <Box p={2}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Payment Type"
                  name="paymentType"
                  value={paymentForm.paymentType}
                  onChange={handleInputChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                >
                  {PaymentTypesData && PaymentTypesData.length > 0 ? (
                    PaymentTypesData.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type.replace('_', ' ')}
                      </MenuItem>
                    ))
                  ) : (
                    <>
                      <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                    </>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Account Holder Name"
                  name="accountHolderName"
                  value={paymentForm.accountHolderName}
                  onChange={handleInputChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Grid>

              {paymentForm.paymentType === 'BANKTRANSFER' ||
              paymentForm.paymentType === 'BANK_TRANSFER' ||
              paymentForm.paymentType === 'NEFT' ||
              paymentForm.paymentType === 'RTGS' ||
              paymentForm.paymentType === 'IMPS' ? (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Account Number"
                      name="accountNumber"
                      value={paymentForm.accountNumber}
                      onChange={handleInputChange}
                      placeholder="Enter account number"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="IFSC Code"
                      name="ifscCode"
                      value={paymentForm.ifscCode}
                      onChange={handleInputChange}
                      placeholder="Enter IFSC code"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="UPI ID"
                    name="upiId"
                    value={paymentForm.upiId}
                    onChange={handleInputChange}
                    placeholder="example@upi"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        }
        actions={
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1.5}
            p={2}
            sx={{ borderTop: 1, borderColor: '#e2e8f0', bgcolor: '#f8fafc' }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseHistoryDialog}
              sx={{ borderRadius: '8px', color: '#64748b', borderColor: '#cbd5e1' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePaymentMethod}
              sx={{
                minWidth: 100,
                borderRadius: '8px',
                bgcolor: '#13519d',
                '&:hover': { bgcolor: '#0d3d75' }
              }}
            >
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </Box>
        }
      />
    </Box>
  );
}

export default Profile;
